import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";

const DEFAULT_TIMEOUT_SECS = 20;

// One opponent card: enters face-down, then flips over to reveal its front.
function StealCard({ id, card, index, isSelected, onSelect }) {
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    // Fire the content swap at the flip's midpoint (edge-on) so the card back
    // is never seen mirrored and the front lands face-up.
    const t = setTimeout(() => setFlipped(true), 450 + index * 130);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div className="[perspective:900px]">
      <motion.div
        initial={{ rotateY: 180, opacity: 0, y: 8 }}
        animate={{ rotateY: 0, opacity: 1, y: 0 }}
        transition={{ delay: 0.2 + index * 0.13, duration: 0.5, ease: "easeInOut" }}
        style={{ transformStyle: "preserve-3d" }}
      >
        {/* Counter-rotated so whichever face shows reads upright, never mirrored. */}
        <div style={{ transform: flipped ? "rotateY(0deg)" : "rotateY(180deg)" }}>
          {flipped ? (
            <button
              onClick={() => onSelect(id)}
              className={`w-full text-left rounded-xl border p-3 transition-all ${
                isSelected
                  ? "border-violet-500/70 bg-violet-500/15"
                  : "border-slate-700 bg-slate-800/60 hover:border-slate-500"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-black text-white">{card.name}</span>
                {isSelected && (
                  <span className="text-[8px] font-black px-1.5 py-0.5 rounded bg-violet-500/20 border border-violet-500/40 text-violet-300 uppercase tracking-wider">
                    Steal
                  </span>
                )}
              </div>
              {card.description && (
                <p className="text-xs text-slate-500 mt-1">{card.description}</p>
              )}
            </button>
          ) : (
            <div className="w-full h-[4.25rem] rounded-xl border border-violet-500/30 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
              <div className="w-6 h-6 rotate-45 rounded-sm border-2 border-violet-500/50" />
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

export default function SubterfugeModal({ availableCardIds, allCards, triggeredBy, onPick, timeoutSecs }) {
  // availableCardIds: string[] — opponent's stealable card IDs, active AND inactive
  // triggeredBy: card id that triggered Subterfuge
  const effectiveTimeout = timeoutSecs > 0 ? timeoutSecs : DEFAULT_TIMEOUT_SECS;
  const [selected, setSelected] = useState(null);
  const [timeLeft, setTimeLeft] = useState(effectiveTimeout);
  const autoFiredRef = useRef(false);

  const cardById = (id) => allCards?.find(c => c.id === id) || { id, name: id, description: "" };
  const triggerCard = cardById(triggeredBy);

  useEffect(() => {
    setTimeLeft(effectiveTimeout);
    autoFiredRef.current = false;
    const first = availableCardIds?.[0] ?? null;
    const id = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (!autoFiredRef.current) {
            autoFiredRef.current = true;
            // Auto-steal first available card; if none, submit null to close.
            onPick(first);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ scale: 0.92, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-slate-900 border border-violet-500/40 rounded-2xl p-5 w-full max-w-sm mx-4 shadow-2xl"
      >
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-[10px] font-black tracking-widest text-violet-400 uppercase">Subterfuge Triggered!</p>
            <span className={`text-xs font-black tabular-nums ${timeLeft <= 5 ? "text-red-400 animate-pulse" : "text-slate-400"}`}>
              {timeLeft}s
            </span>
          </div>
          <p className="text-sm text-slate-400">
            Opponent played <span className="text-white font-bold">{triggerCard.name}</span> — their card is discarded. Now steal one of their cards.
          </p>
        </div>

        <div className="space-y-2 mb-5 max-h-60 overflow-y-auto">
          {availableCardIds.length === 0 && (
            <p className="text-slate-500 text-sm text-center py-4">Opponent has no cards to steal.</p>
          )}
          {availableCardIds.map((id, i) => (
            <StealCard
              key={id}
              id={id}
              index={i}
              card={cardById(id)}
              isSelected={selected === id}
              onSelect={setSelected}
            />
          ))}
        </div>

        <button
          onClick={() => selected && onPick(selected)}
          disabled={!selected && availableCardIds.length > 0}
          className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-black text-sm transition disabled:opacity-40"
        >
          {availableCardIds.length === 0
            ? "Nothing to steal — close"
            : selected
            ? "Steal This Card →"
            : "Select a card to steal"}
        </button>
      </motion.div>
    </div>
  );
}
